/* eslint-disable @typescript-eslint/explicit-module-boundary-types */
import type { Dispatch, PropsWithChildren } from 'react';
import { createContext, useContext, useReducer } from 'react';
import type { Nullable } from '~/utilities/type-guards';
import type { MediaType, PublicCommentsSelection } from '~/graphql';
import type { MediaPageCustomizationFeatureStrings } from '~/singlePageApp/screens/MediaPreview/customize/hooks/useMediaPageCustomizationsFeatures';

export type FontWithNullableUrl = { family: string; url?: string | null };

export type MediaPageCustomizations = {
  // Dark (000000), Light (ffffff), or Custom
  letterboxColor: string;
  backgroundImage: { imageUrl?: string; enabled: boolean };
  primaryColor: string;
  fontFamily: FontWithNullableUrl['family'];
  fontUrl?: FontWithNullableUrl['url'];
  fontSize: number;
  shouldShowTitle: boolean;
  shouldShowDescription: boolean;
  shouldShowTranscript: boolean;
  shouldShowSpeakers: boolean;
  // this is nullable because we need to know if this has ever been set
  // so that we can override it with the legacy media's embed_options show_comments setting
  publicCommentsSelection?: PublicCommentsSelection;
  // TODO: add more customizations like password protection, resources, etc
};

type MediaPageCustomizationReducerActions =
  | { type: 'FAILED_TO_SAVE_CUSTOMIZATIONS' }
  | { type: 'INIT_CUSTOMIZATIONS'; payload: MediaPageCustomizations }
  | { type: 'RESET_CUSTOMIZATIONS' }
  | { type: 'RESET_UPGRADE_CUSTOMIZATIONS'; payload: Partial<MediaPageCustomizations> }
  | { type: 'SAVED_CUSTOMIZATIONS' }
  | { type: 'SAVING_CUSTOMIZATIONS' }
  | { type: 'SET_DOES_MEDIA_HAVE_SPEAKERS'; payload: boolean }
  | { type: 'SET_DOES_MEDIA_HAVE_TRANSCRIPT'; payload: boolean }
  | { type: 'SET_FEATURES'; payload: MediaPageCustomizationFeatureStrings[] }
  | { type: 'SET_HASHED_ID'; payload: string | undefined }
  | { type: 'SET_MEDIA_TYPE'; payload: MediaType }
  | { type: 'SET_PLAYER_COLOR'; payload: string }
  | { type: 'UPDATE_FONT_SIZE'; payload: number }
  | { type: 'UPDATE_FONT'; payload: FontWithNullableUrl }
  | { type: 'UPDATE_LETTERBOX_COLOR'; payload: string }
  | { type: 'UPDATE_PUBLIC_COMMENTS_SELECTION'; payload: PublicCommentsSelection }
  | { type: 'UPDATE_SHOULD_SHOW_DESCRIPTION'; payload: boolean }
  | { type: 'UPDATE_SHOULD_SHOW_SPEAKERS'; payload: boolean }
  | { type: 'UPDATE_SHOULD_SHOW_TITLE'; payload: boolean }
  | { type: 'UPDATE_SHOULD_SHOW_TRANSCRIPT'; payload: boolean };

type MediaPageCustomizationState = {
  hashedId: string | undefined;
  savedCustomizations: MediaPageCustomizations;
  currentCustomizations: MediaPageCustomizations;
  doesMediaHaveTranscript: boolean;
  doesMediaHaveSpeakers: boolean;
  hasJustSaved: boolean;
  isSaving: boolean;
  mediaType: MediaType;
  playerColor: string;
  features: string[];
  // a map of customizations that have been modified since the last save with their values
  modifiedCustomizations: Map<keyof MediaPageCustomizations, string>;
};

// NOTE: add more default media page customizations here as we go!
export const DEFAULT_CUSTOMIZATIONS: MediaPageCustomizations = {
  letterboxColor: '',
  backgroundImage: { enabled: false },
  fontFamily: 'Inter',
  fontSize: 1,
  primaryColor: '',
  shouldShowTitle: true,
  shouldShowDescription: true,
  shouldShowTranscript: false,
  shouldShowSpeakers: true,
};

type MediaPageCustomizationDispatchType = Dispatch<MediaPageCustomizationReducerActions>;
type MediaPageCustomizationContextType = MediaPageCustomizationState | Record<string, never>;

const MediaPageCustomizationContext = createContext<
  MediaPageCustomizationState | Record<string, never>
>({});

const MediaPageCustomizationDispatchContext = createContext<MediaPageCustomizationDispatchType>(
  () => null,
);

export const useMediaPageCustomizations = (): MediaPageCustomizationContextType => {
  return useContext(MediaPageCustomizationContext);
};

// There are some cases where we'll want to use the customizations context but we aren't sure if we'll be within
// the relevant provider (namely, if we're using them on the AnonymousMediaScreen).
export const useNullableMediaPageCustomizations =
  (): Nullable<MediaPageCustomizationContextType> => {
    return useMediaPageCustomizations() as Nullable<MediaPageCustomizationContextType>;
  };

export const useMediaPageCustomizationDispatch = (): MediaPageCustomizationDispatchType => {
  return useContext(MediaPageCustomizationDispatchContext);
};

const mediaPageCustomizationReducer = (
  state: MediaPageCustomizationState,
  action: MediaPageCustomizationReducerActions,
): MediaPageCustomizationState => {
  switch (action.type) {
    case 'SET_HASHED_ID': {
      return {
        ...state,
        hashedId: action.payload,
      };
    }
    case 'SET_MEDIA_TYPE': {
      return {
        ...state,
        mediaType: action.payload,
      };
    }
    case 'SET_PLAYER_COLOR': {
      return {
        ...state,
        playerColor: action.payload,
      };
    }
    case 'SET_FEATURES': {
      return {
        ...state,
        features: action.payload,
      };
    }
    case 'SET_DOES_MEDIA_HAVE_TRANSCRIPT': {
      return {
        ...state,
        doesMediaHaveTranscript: action.payload,
      };
    }
    case 'SET_DOES_MEDIA_HAVE_SPEAKERS': {
      return {
        ...state,
        doesMediaHaveSpeakers: action.payload,
      };
    }
    case 'INIT_CUSTOMIZATIONS': {
      return {
        ...state,
        // What is returned from the database might not be a complete set of customizations, so we need to merge
        // the defaults in to ensure we track all of the customizations properly as they change.
        savedCustomizations: {
          ...DEFAULT_CUSTOMIZATIONS,
          ...action.payload,
        },
        currentCustomizations: {
          ...DEFAULT_CUSTOMIZATIONS,
          ...action.payload,
        },
        hasJustSaved: false,
        isSaving: false,
        modifiedCustomizations: new Map(),
      };
    }
    case 'RESET_CUSTOMIZATIONS': {
      return {
        ...state,
        currentCustomizations: { ...state.savedCustomizations }, // Don't accidentally mutate savedCustomizations
        hasJustSaved: false,
        isSaving: false,
        modifiedCustomizations: new Map(),
      };
    }
    case 'RESET_UPGRADE_CUSTOMIZATIONS': {
      return {
        ...state,
        currentCustomizations: {
          ...state.currentCustomizations,
          ...action.payload,
        },
      };
    }
    case 'UPDATE_LETTERBOX_COLOR':
      return {
        ...state,
        currentCustomizations: {
          ...state.currentCustomizations,
          letterboxColor: action.payload,
        },
        hasJustSaved: false,
        isSaving: false,
      };
    case 'UPDATE_SHOULD_SHOW_DESCRIPTION':
      return {
        ...state,
        currentCustomizations: {
          ...state.currentCustomizations,
          shouldShowDescription: action.payload,
        },
        hasJustSaved: false,
        isSaving: false,
      };
    case 'UPDATE_SHOULD_SHOW_TITLE':
      return {
        ...state,
        currentCustomizations: {
          ...state.currentCustomizations,
          shouldShowTitle: action.payload,
        },
        hasJustSaved: false,
        isSaving: false,
      };
    case 'UPDATE_SHOULD_SHOW_TRANSCRIPT':
      return {
        ...state,
        currentCustomizations: {
          ...state.currentCustomizations,
          shouldShowTranscript: action.payload,
        },
        hasJustSaved: false,
        isSaving: false,
      };
    case 'UPDATE_PUBLIC_COMMENTS_SELECTION':
      return {
        ...state,
        currentCustomizations: {
          ...state.currentCustomizations,
          publicCommentsSelection: action.payload,
        },
        hasJustSaved: false,
        isSaving: false,
      };
    case 'UPDATE_FONT':
      return {
        ...state,
        currentCustomizations: {
          ...state.currentCustomizations,
          fontFamily: action.payload.family,
          fontUrl: action.payload.url,
        },
        hasJustSaved: false,
        isSaving: false,
      };
    case 'UPDATE_FONT_SIZE':
      return {
        ...state,
        currentCustomizations: {
          ...state.currentCustomizations,
          fontSize: action.payload,
        },
        hasJustSaved: false,
        isSaving: false,
      };
    case 'UPDATE_SHOULD_SHOW_SPEAKERS':
      return {
        ...state,
        currentCustomizations: {
          ...state.currentCustomizations,
          shouldShowSpeakers: action.payload,
        },
        hasJustSaved: false,
        isSaving: false,
        modifiedCustomizations: new Map(state.modifiedCustomizations).set(
          'shouldShowSpeakers',
          action.payload.toString(),
        ),
      };
    case 'SAVED_CUSTOMIZATIONS':
      return {
        ...state,
        hasJustSaved: true,
        isSaving: false,
        // We need to update savedCustomizations to match currentCustomizations so that we don't lose track of
        // any customizations that have been made since the last save.
        savedCustomizations: state.currentCustomizations,
        modifiedCustomizations: new Map(),
      };
    case 'FAILED_TO_SAVE_CUSTOMIZATIONS':
      return {
        ...state,
        isSaving: false,
      };
    case 'SAVING_CUSTOMIZATIONS':
      return {
        ...state,
        hasJustSaved: false,
        isSaving: true,
      };

    default:
      return state;
  }
};

// allow us to override any customizations we want, added namely for testing
type MediaPageCustomizationsProviderProps = PropsWithChildren<{
  initialCustomizations?: Partial<MediaPageCustomizations>;
}>;

export const MediaPageCustomizationsProvider = ({
  children,
  initialCustomizations = DEFAULT_CUSTOMIZATIONS,
}: MediaPageCustomizationsProviderProps) => {
  const [mediaPageCustomizations, mediaPageCustomizationDispatch] = useReducer(
    mediaPageCustomizationReducer,
    {
      hashedId: '',
      savedCustomizations: initialCustomizations as MediaPageCustomizations,
      currentCustomizations: initialCustomizations as MediaPageCustomizations,
      doesMediaHaveTranscript: false,
      doesMediaHaveSpeakers: false,
      hasJustSaved: false,
      isSaving: false,
      mediaType: 'UnknownType',
      playerColor: '2949E5',
      features: [],
      modifiedCustomizations: new Map(),
    },
  );

  return (
    <MediaPageCustomizationContext.Provider value={mediaPageCustomizations}>
      <MediaPageCustomizationDispatchContext.Provider value={mediaPageCustomizationDispatch}>
        {children}
      </MediaPageCustomizationDispatchContext.Provider>
    </MediaPageCustomizationContext.Provider>
  );
};
